// ─────────────────────────────────────────────
//  CaseBar — ONE room row on the Timeline chart: one blue box, one or two
//  people inside it.
//
//  Lifted out of `TimelineChartPage` (1900+ lines, already 3.8x the 500-line
//  house rule) rather than added to it. The page keeps the row, the section tint
//  and the lane; everything a bar knows about itself lives here and in its four
//  siblings — `CaseBarShell` (the box and its a11y structure), `ProviderBand`
//  (one person's line and hatch), `BarName` (the name, and the menu it opens)
//  and `useBarDrag` (the gesture).
//
//  ── WHAT THE OWNER DECIDED, 2026-08-20 ────────────────────────────────────
//
//  1. ONE BOX. A room with two providers is still ONE case. They are bands
//     inside the bar — *"the 2 CRNA need to be inside the blue box"* — and the
//     surgeon · supervising-MD line stays under them: *"inside is missing the
//     supervisor and the surgeon."*
//
//  2. THE ROW GROWS FOR TWO AND SHRINKS BACK FOR ONE. *"If you want to expand
//     the room is ok, as soon as we remove the extra CRNA come to normal size."*
//     Height comes from `caseLanes.rowHeightFor`; the page asks per room.
//     ⚠️ A SINGLE-PROVIDER ROOM IS PIXEL-IDENTICAL TO WHAT IT ALWAYS DREW —
//     that is 17 of his 19 rooms, and it is why the two branches below are
//     genuinely separate rather than one parameterised layout.
//
//  3. NO HIERARCHY. *"i dont care who is orienting who, place the 2 crnas."*
//     Both bands are the same height, type and weight; no indent, no `↳`, no
//     "orienting" in the tooltip or the spoken name. ⚠️ DISPLAY ONLY — the
//     seat (`ahpId`) and the second-provider fields stay distinct in storage,
//     because a write has to know which slot it is updating and the seat still
//     holds the room for `isSoloCase`.
//
//  4. EACH PERSON GETS THEIR OWN DIAGONAL HATCH, ending at their own leaving
//     time. *"We are missing the diagonal lines that show the leaving time for
//     each CRNA."*
//
//  5. The vertical drag-affordance rules at the bar's edges are gone. The hit
//     zones, pointer capture, keyboard sliders, focus ring and `col-resize`
//     hover cursor all stay — see `caseBarStyles.handle`.
//
//  6. Clicking a NAME opens the quick-actions menu for THAT person; clicking
//     the rest of the bar opens the case modal; the edges still drag.
//
//  ⚠️ THE DRAG MUST NEVER COST ANYONE THE CLICK. The bar is a click target that
//  opens `TimelineCaseModal`, which carries both time dropdowns. That modal IS
//  the WCAG 2.2 SC 2.5.7 single-pointer alternative to this drag, so every
//  refusal below (no permission, an on-call pending bar, a lane that has not
//  measured, a sub-threshold wobble) leaves the bar clickable.
//
//  ⚠️ NO ARITHMETIC LIVES HERE. Minutes come out of `domain/timelineDrag`,
//  boxes out of `domain/caseLanes`, and every hatch percent out of the ONE
//  `shiftHatchPercent`. jsdom has no layout, so maths done in a component
//  cannot be tested.
// ─────────────────────────────────────────────
import React, { useRef } from "react";
import {
  barMetrics,
  crnaSeatKey,
  hmToMinutes,
  shiftHatchPercent,
  shiftLabelColor,
} from "../../domain/timeline";
import { barBands, hasSecondProvider, nameHitPad, secondCoverage } from "../../domain/caseLanes";
import {
  LABEL_CHAR_PX,
  SUB_MIN_PX,
  caseBarPeople,
  caseBarSpeech,
  fitBarName,
  fitInlineChip,
  labelBudgetPx,
  stripRoleSuffix,
} from "../../domain/caseBarLabel";
import { fitCaseSub } from "../../domain/caseBarSub";
import useBarDrag from "./useBarDrag";
import CaseBarShell, { HANDLE_W } from "./CaseBarShell";
import ProviderBand from "./ProviderBand";
import BarName from "./BarName";
import S from "./caseBarStyles";

// Below this the two handles would eat the whole bar and leave nothing to click.
// `S.bar` has `minWidth: 40`, so a 15-minute case on a 10-hour axis is drawn
// WIDER than its data and is exactly the bar this protects: one handle (the end),
// and the body stays a click target.
const NARROW_BAR_PX = 2 * HANDLE_W + 8;

// An on-call row with no parseable start time is drawn spanning the WHOLE axis
// regardless of what it says (timeline.js). Its pixels are the axis, not its
// data, so a drag would convert a chart decision into a clinical time nobody
// entered. This is the single most important refusal in the file.
export function isPendingOnCall(c) {
  return Boolean(c && c.onCall && hmToMinutes(c.startTime) == null);
}

export default function CaseBar({
  c,
  roomName,
  tint,
  isMobile = false,
  axisStart,
  span,
  // May this bar be dragged at all? The page folds role, connection band and
  // preview state into one boolean — see `panelAssign.writesAllowed`.
  dragAllowed = false,
  // May the bar be opened? Separate on purpose: losing the drag must not lose
  // the modal.
  canOpen = false,
  // Measured width of the room track, in px. 0/absent before the first layout —
  // both handles are rendered then, which is right for all but the shortest bar.
  trackWidth = 0,
  onOpen,
  // ({ caseRow, startTime, endTime, source }) → Promise. Everything after the
  // gesture — validate, write, undo window, notify — belongs to the page hook.
  onCommit,
  // ({ caseRow, departTime, person, source }) → Promise. The SECOND provider's
  // hatch boundary, dragged.
  // ⚠️ A DIFFERENT WRITE ENTIRELY: it moves that person's leaving time
  // (`orienteeDepartTime`), never the room's case window. Absent → no handle at
  // all rather than one that writes the seat's times.
  onSecondCommit,
  // ({ id, key, name, isSecond }, caseRow, triggerRef) → void. Absent → the
  // names render as the plain spans they have always been (a wall display, an
  // observer). `id` is NULL for a person nobody resolved; `key` is always set
  // and identifies which of the bar's two names was pressed.
  onNameMenu,
  // The menu KEY the quick-actions menu is currently open for, for
  // `aria-expanded`. Not the id — an unresolved person has none.
  openMenuFor = null,
}) {
  const seatNameRef = useRef(null);
  const secondNameRef = useRef(null);

  const pending = isPendingOnCall(c);
  const draggable = dragAllowed && !pending;

  // ── The people ────────────────────────────────────────────────────────────
  const { seat, second, inlineSecond, sub } = caseBarPeople(c);
  const coverage = hasSecondProvider(c) ? secondCoverage(c) : null;
  const paired = Boolean(second && coverage);
  const bands = barBands({ paired, isMobile });

  // ── The case window's gesture: unchanged ──────────────────────────────────
  const seatDrag = useBarDrag({
    draggable,
    axisStart,
    span,
    startMin: c.startMin,
    endMin: c.endMin,
    startTime: c.startTime,
    endTime: c.endTime,
    labelFor: (edge) => `${roomName || "This room"} case ${edge === "start" ? "start" : "end"} time`,
    onCommit: ({ startTime, endTime, source }) => {
      if (onCommit) onCommit({ caseRow: c, startTime, endTime, source });
    },
  });

  // ── The second provider's gesture: THEIR leaving time ─────────────────────
  //
  // ⚠️ ONE EDGE, AND ONLY ONE. There is no `orienteeStartTime` anywhere in this
  // schema — a second provider arrives with the case, and the single fact the
  // day records about their hours is when they LEAVE. A start handle here would
  // have to write something, and the only thing it could write is the room's
  // case start, which is the other provider's fact and not theirs. So this
  // drags the END of their coverage and nothing else, and the reason is in the
  // aria-label.
  const secondDrag = useBarDrag({
    draggable: draggable && Boolean(onSecondCommit) && paired,
    axisStart,
    span,
    startMin: coverage ? coverage.startMin : c.startMin,
    endMin: coverage ? coverage.endMin : c.endMin,
    labelFor: () =>
      `${second ? stripRoleSuffix(second.name) : "Second provider"} leaving time in ${roomName || "this room"}`,
    onCommit: ({ endTime, source }) => {
      if (onSecondCommit) onSecondCommit({ caseRow: c, departTime: endTime, person: c.orientee, source });
    },
  });

  // ── Geometry ──────────────────────────────────────────────────────────────
  const box = barMetrics(seatDrag.liveStart, seatDrag.liveEnd, axisStart, span);
  // A minute as a percent of the TRACK, which is the space the tooltip lives in.
  // Expressed through `barMetrics` rather than re-derived, so there is still
  // exactly one place that turns minutes into chart percent.
  const axisPct = (min) => barMetrics(axisStart, min, axisStart, span).width;

  // ⚠️ MEASURED FROM THE COMMITTED WINDOW, NOT THE GHOST. Sizing this off the
  // candidate makes the handle you are currently holding vanish the moment you
  // squeeze the bar under the threshold — the control disappears mid-gesture and
  // a keyboard user loses focus to the body.
  const committed = barMetrics(c.startMin, c.endMin, axisStart, span).width;
  const barPx = trackWidth > 0 ? (committed / 100) * trackWidth : null;
  // Unknown width → assume there is room. The measured case is the one that
  // matters and it arrives on the first layout.
  const narrow = barPx != null && barPx < NARROW_BAR_PX;

  // ── The hatches ───────────────────────────────────────────────────────────
  //
  // ⚠️ ONE FORMULA, ASKED TWICE. `shiftHatchPercent` is the board's single
  // answer to "how far across this bar does that person's shift reach"; the
  // second provider gets it by handing in THEIR departure against the same case
  // window, not by a second copy of the arithmetic here. An open-ended coverage
  // (no leaving time recorded) yields 0 and therefore no hatch at all — an
  // unhatched line reads "still here", which is the truth.
  const seatHatch = shiftHatchPercent(c);
  const secondHatch =
    paired && !coverage.openEnded
      ? shiftHatchPercent({
          provider: second.name,
          startMin: c.startMin,
          endMin: c.endMin,
          departMin: coverage.endMin,
        })
      : 0;

  // ── What actually fits ────────────────────────────────────────────────────
  //
  // ⚠️ THE NAME OUTRANKS EVERY ANNOTATION. The owner's earlier complaint was a
  // bar reading "Thomas Klaph…": it had spent its whole width on the one thing
  // it then failed to deliver. `fitBarName` walks a fixed ladder — role suffix,
  // then departure, then the given name, then everything but the surname — and
  // only the surname is ever left to the browser's ellipsis.
  //
  // ⚠️ BOTH PEOPLE ARE MEASURED THE SAME WAY. They are drawn in the same type
  // now, so they degrade on the same ladder with the same per-character
  // estimate; the second name used to be measured at the smaller `SUB_CHAR_PX`
  // because it was a subordinate line, and it no longer is one.
  const budget = labelBudgetPx(barPx);
  const seatFit = fitBarName({ name: seat.name, depart: seat.depart12h, budgetPx: budget, charPx: LABEL_CHAR_PX });
  const secondFit = paired
    ? fitBarName({ name: second.name, depart: second.depart12h, budgetPx: budget, charPx: LABEL_CHAR_PX })
    : null;
  // ⚠️ AND THE SUB-LINE DEGRADES TOO, BECAUSE `text-overflow: ellipsis` CLIPS
  // FROM THE RIGHT AND THE MD IS ON THE RIGHT. `buildTimeline` pre-joins
  // "Procedure · Surgeon · ⚕ MD" into one string, so every bar too narrow for
  // the whole line dropped the SUPERVISING DOCTOR and kept the procedure —
  // backwards, since the doctor is the name a board-runner needs. Measured on
  // the owner's live board: WC TEE (83px of bar, 157px of line) and WOR 10
  // (181px of bar) both lost their MD. `fitCaseSub` re-joins the row's separate
  // fields at a length that fits, dropping the procedure, then the surgeon, then
  // the on-call tag, and only then abbreviating the doctor.
  //
  // "then the surgeon": a bar too narrow to hold a useful sub-line drops it
  // whole rather than printing two characters and an ellipsis. Mobile has never
  // had room for it.
  //
  // ⚠️ `paired` IS NOT A REASON TO DROP IT ANY MORE. It used to be — the second
  // person took this line — and the owner caught the loss immediately. A paired
  // row now buys the line a band of its own.
  const subFit = fitCaseSub(c, budget);
  const showSub = Boolean(subFit.text) && !isMobile && (barPx == null || barPx >= SUB_MIN_PX);
  // The trainee riding on the seat holder's bar (owner, 2026-09-07). Fitted to
  // what is left of the line AFTER the seat's own name has taken its width —
  // `fitInlineChip` drops the chip whole rather than draw a stub, because it is
  // a CONTROL now and a clipped control is one nobody can press.
  const inlineFit = inlineSecond
    ? fitInlineChip({ name: inlineSecond.name, seat: seatFit, budgetPx: budget })
    : null;

  // The undegraded sentence, and the bar's accessible name. Built from the full
  // names, so a 40px bar reading "Klaphake" still announces "Thomas Klaphake"
  // and the person with him — evenly, with no rank.
  const speech = caseBarSpeech(c, roomName);

  // ── Clicks ────────────────────────────────────────────────────────────────
  //
  // ⚠️ ONE SUPPRESSION FLAG PER GESTURE, CONSUMED ONCE. The browser synthesises
  // a click after a real drag; whichever control it lands on — the bar's open
  // target or a name — takes the flag and does nothing. A second mechanism for
  // the name would drift from this one the first time either changed.
  const consumed = (drag) => {
    if (!drag.suppressClickRef.current) return false;
    drag.suppressClickRef.current = false;
    return true;
  };

  const openFrom = (drag) => (opts) => {
    if (!opts?.fromKeyboard && consumed(drag)) return;
    if (onOpen) onOpen(c);
  };

  // ⚠️ AN UNRESOLVED NAME IS NOW A CONTROL TOO, AND THAT WAS A REVERSAL.
  //
  // It used to be "no id, no menu": `BenchAssignFlow` writes roster documents
  // keyed by the provider id, a legacy "X orient Y" cell names two people and
  // identifies neither, and a menu whose every option wrote to `undefined` is
  // worse than no menu. Correct at the time — and it is exactly what the owner
  // reported as *"some crnas in the timeline when you press dont work"*, because
  // from the operator's side a name that opens nothing is indistinguishable from
  // a broken one.
  //
  // What changed is that the menu now has an option that needs NO id: "Edit
  // CRNA" writes the assignment row's SLOT (`ahpDepartTime` /
  // `orienteeDepartTime`), addressed by which chair the person is in. So the
  // trigger is offered whenever the viewer may edit, and `domain/assignChoices`
  // — not this file — decides that an unidentified person is offered that one
  // option and none of the id-keyed ones.
  //
  // ⚠️ THE ID IS PASSED AS NULL WHEN IT IS NULL. `menuKey` exists only so two
  // unresolved names on one bar can be told apart for `aria-expanded`; it must
  // never be substituted for the id, or a synthetic `sp:<name>` reaches a write.
  //
  // ⚠️ AND THE MENU IS HANDED A CASE THAT STILL CARRIES THE TRAINEE.
  // `TimelineChartPage.barCase` nulls `orientee` and re-hands the person on
  // `inlineOrientee`, because `orientee` is what `caseLanes` and `roomRowHeight`
  // read and the row has to stay 32px. But it is ALSO what `BenchAssignFlow`
  // reads — through `asAssignmentRow` → `secondProviderOf` → `matchesSecondProvider`
  // — to answer "which chair is this person sitting in". Handed the stripped
  // case, the menu resolves NO second provider, so the trainee is filed as the
  // SEAT holder: her menu offers "Assign Supervising CRNA" (which would write
  // over herself) and "Edit CRNA" seeds the wrong slot's leaving time. The
  // display strip and the write's addressing are two different questions asked
  // of one field; this restores the field for the half that is about the write.
  const menuCase = c.inlineOrientee ? { ...c, orientee: c.inlineOrientee } : c;

  const menuFor = ({ drag, id, menuKey, name, ref, isSecond }) => {
    if (!onNameMenu) return null;
    const clean = stripRoleSuffix(name);
    return {
      label: `Quick actions — ${clean}`,
      expanded: openMenuFor != null && openMenuFor === menuKey,
      triggerRef: ref,
      open: () => {
        if (consumed(drag)) return;
        onNameMenu({ id: id || null, key: menuKey, name: clean, isSecond }, menuCase, ref);
      },
    };
  };

  const seatMenu = menuFor({
    drag: seatDrag,
    id: c.ahpId,
    // `crnaSeatKey` is the board's own answer to "how is this seat identified
    // when nobody resolved an id" — the seat NAME, never the two-person cell.
    // The row id is the last resort, so a nameless seat still gets a distinct
    // trigger rather than sharing one with the person below it.
    menuKey: c.ahpId || crnaSeatKey(c) || `seat:${c.id}`,
    name: seat.name,
    ref: seatNameRef,
    isSecond: false,
  });
  // ⚠️ THE TRAINEE'S OWN TRIGGER, AND THIS IS THE BUG THE OWNER REPORTED ON
  // 2026-09-09: *"you have wo2: michael peterson and cheryl martin. Both name
  // need to be able to be clicked. Only Michael i can click. So i can move them
  // and reassign them"*. The chip was drawn as a `suffix` INSIDE the seat
  // holder's `<button>`, so a press on the trainee's name was a press on HIS
  // control — she had no way to be moved, reassigned or sent home from the
  // chart at all, and the bar looked like it worked.
  //
  // `secondNameRef` is reused rather than a third ref added: a bar is either
  // PAIRED (two bands) or INLINE (one line, a chip) — `barCase` moves the person
  // from `orientee` to `inlineOrientee`, so the two are mutually exclusive and
  // one of these menus is always null.
  const inlineMenu =
    inlineFit && inlineFit.text
      ? menuFor({
          drag: seatDrag,
          id: c.inlineOrientee?.id,
          // Same key shape as the paired band's: `id || sp:<name>`, so an
          // unresolved trainee still gets a trigger distinct from the seat's and
          // `aria-expanded` can tell the two apart.
          menuKey: c.inlineOrientee?.id || c.inlineOrientee?.key || `second:${c.id}`,
          name: inlineSecond.name,
          ref: secondNameRef,
          isSecond: true,
        })
      : null;
  const secondMenu = paired
    ? menuFor({
        drag: secondDrag,
        id: c.orientee?.id,
        // `orientee.key` is already `id || sp:<name>` — the same key the roster
        // and `matchesSecondProvider` use, so the menu resolves to the right
        // slot even with no id anywhere on the row.
        menuKey: c.orientee?.id || c.orientee?.key || `second:${c.id}`,
        name: second.name,
        ref: secondNameRef,
        isSecond: true,
      })
    : null;

  // ── The tooltip ───────────────────────────────────────────────────────────
  //
  // ⚠️ IT CARRIES WHAT THE WIDTH COULD NOT — both people in full, both
  // departures and the surgeon / MD line, UNDEGRADED. `sub`, never `subFit`:
  // the ladder above is a visual fit, not a decision to withhold a fact, so the
  // hover text and `caseBarSpeech` still carry the procedure and the surgeon a
  // narrow bar had to drop. A fact displaced off the bar must never be a fact
  // dropped from the page. Both people are written the same
  // way, with no mark and no relationship word.
  const line = (n, d) => `${n}${d ? ` (leaves ${d})` : ""}`;
  const title =
    `${roomName} · ${c.start12h}–${c.end12h}` +
    `\n${line(seat.name, seat.depart12h)}` +
    (second ? `\n${line(second.name, second.depart12h)}` : "") +
    // ⚠️ THE CHIP IS DROPPED ON A NARROW BAR, THE TOOLTIP IS NOT. No leaving
    // time: the inline person's hour is not resolved onto this object and
    // borrowing the seat holder's would invent one.
    (inlineSecond ? `\n${stripRoleSuffix(inlineSecond.name)} (orientee)` : "") +
    (sub ? `\n${sub}` : "") +
    (c.attending ? `\nMD: ${c.attending}` : "") +
    (canOpen ? "\n\nClick to edit times / relieve" : "") +
    (draggable ? "\nDrag either edge to reschedule" : "") +
    (paired && draggable && onSecondCommit
      ? `\nDrag ${stripRoleSuffix(second.name)}'s edge to set when they leave`
      : "");

  // ── The gesture in flight ─────────────────────────────────────────────────
  const active = seatDrag.ghost
    ? {
        tipMin: seatDrag.activeEdge === "start" ? seatDrag.ghost.startMin : seatDrag.ghost.endMin,
        tipLeft: seatDrag.activeEdge === "start" ? box.left : box.left + box.width,
      }
    : secondDrag.ghost
      ? { tipMin: secondDrag.liveEnd, tipLeft: axisPct(secondDrag.liveEnd) }
      : { tipMin: null, tipLeft: 0 };

  // The second provider's handle rides ON their hatch boundary — the thing you
  // grab is the thing you see. With no leaving time recorded there is no
  // boundary, so it parks at the far end of the case ("they are here until it
  // ends, as far as anyone has said") and dragging it left records one.
  const extraHandles =
    paired && draggable && onSecondCommit
      ? [
          {
            key: "second-depart",
            props: secondDrag.handleProps("end"),
            leftPercent: coverage.openEnded ? 100 : axisBoundaryPercent(secondDrag, c, secondHatch),
            top: bands.second.top,
            height: bands.second.height,
            focused: secondDrag.focusEdge === "end",
          },
        ]
      : [];

  return (
    <CaseBarShell
      barId={c.id}
      left={box.left}
      width={box.width}
      tint={tint}
      onCall={c.onCall}
      canOpen={canOpen}
      onOpen={openFrom(seatDrag)}
      speech={speech}
      title={title}
      gesturing={seatDrag.gesturing || secondDrag.gesturing}
      draggable={draggable}
      edges={narrow ? ["end"] : ["start", "end"]}
      handleProps={seatDrag.handleProps}
      focusEdge={seatDrag.focusEdge}
      extraHandles={extraHandles}
      tipMin={active.tipMin}
      tipLeft={active.tipLeft}
    >
      {paired ? (
        <>
          {/* Two people, two bands, one box. Same height, same type — order is
              stable, rank is not shown. */}
          <ProviderBand
            band={bands.seat}
            hatchPercent={seatHatch}
            name={seatFit.name}
            depart={seatFit.depart}
            color={shiftLabelColor(c.depart12h)}
            menu={seatMenu}
          />
          <ProviderBand
            band={bands.second}
            hatchPercent={secondHatch}
            name={secondFit.name}
            depart={secondFit.depart}
            color={shiftLabelColor(second.depart12h)}
            menu={secondMenu}
          />
          {bands.sub && showSub && (
            <div style={{ ...S.subBand, top: bands.sub.top, height: bands.sub.height }}>
              <span aria-hidden="true" style={{ ...S.barSub, lineHeight: `${bands.sub.height}px` }}>
                {subFit.text}
              </span>
            </div>
          )}
        </>
      ) : (
        <>
          {/* ⚠️ THE SINGLE-PROVIDER BAR IS UNTOUCHED, AND DELIBERATELY NOT
              EXPRESSED THROUGH THE BANDS ABOVE. 17 of the owner's 19 rooms are
              looking at this markup all day; re-deriving it from the paired
              layout would move it by a pixel or two the day either changed, and
              he asked explicitly for it to "come to normal size".

              Shift-end hatching: diagonals across the TOP HALF only, ending
              where this CRNA is scheduled to leave. Two zones split by a
              straight rule — VERY FAINT under the name, so the name still
              reads, then DARKER from that rule out to the departure, where it
              lies over empty bar and can obscure nothing. The faint zone is
              sized by a hidden CLONE of the label (same text, same fonts,
              `color: transparent`), so the divider lands exactly where the real
              text ends with no runtime measuring. The bottom half stays clear
              for the surgeon / supervisor sub-line. */}
          {seatHatch > 0 && (
            <span aria-hidden="true" style={{ ...S.barHatch, width: `${seatHatch}%` }}>
              <span style={S.barHatchFaint}>
                {seatFit.name}
                {seatFit.depart && <span style={S.barDepart}> {seatFit.depart}</span>}
                {/* ⚠️ THE CLONE HAS TO CARRY THE CHIP TOO. This hidden copy is
                    what sizes the FAINT half of the hatch — the divider lands
                    where the text ends, and everything past it is the DARK
                    zone. Measured in a browser at 1000px: without the chip here
                    the divider sat at the seat holder's name and the trainee's
                    control was drawn over 0.22-alpha diagonals. It read as
                    decoration and it was the harder of the two names to see,
                    which is the opposite of the fix. */}
                {inlineFit && inlineFit.text ? (
                  <span style={S.barOrienteeChip}> {inlineFit.text}</span>
                ) : null}
              </span>
              <span style={S.barHatchDark} />
            </span>
          )}
          {/* CRNA name colour-coded by shift (3/5/7/9 PM → amber/blue/violet/red;
              gray otherwise). Bar background keeps the section tint.

              ⚠️ AND WHEN IT IS A CONTROL, IT IS A LEGAL-SIZED ONE. The trigger
              measured 186 x 13 on the deployed board — half of WCAG 2.2 SC
              2.5.8's 24px floor. `nameHitPad` pads the box out to the bar's full
              inner height (26px desktop) and cancels every one of those pixels
              with an equal negative margin, so this stays a 12.65px line as far
              as the flex column is concerned and the surgeon line under it does
              not move. The row height is the owner's and is not up for
              negotiation; see `caseLanes.nameHitPad` for why a symmetric
              `padding: 6px 0` would have been clipped down to a 20px target.

              ⚠️ ONLY WHEN THERE IS A MENU. `menu == null` renders the plain
              `aria-hidden` span a wall display has always drawn, and a span is
              not a target — padding it would buy nothing and put a dead box over
              the surgeon line for no reason. */}
          {/* ⚠️ TWO NAMES, TWO CONTROLS, ONE LINE. The trainee's chip is a
              `BarName` of its own beside the seat holder's, never a `suffix`
              inside it — see `caseBarStyles.barOrienteeChip`. The row is
              `position: static`, so the bar's open-target (absolute, zIndex 1)
              still takes every pixel neither name covers. */}
          <div style={S.barNameLine}>
            <BarName
              text={seatFit.name}
              depart={seatFit.depart}
              style={{
                ...S.barLabel,
                color: shiftLabelColor(c.depart12h),
                ...(seatMenu ? nameHitPad({ isMobile, subShown: showSub }) : null),
              }}
              menu={seatMenu}
            />
            {inlineFit && inlineFit.text && (
              <BarName
                text={inlineFit.text}
                style={{
                  ...S.barLabel,
                  ...S.barOrienteeChip,
                  color: shiftLabelColor(c.depart12h),
                  ...(inlineMenu ? nameHitPad({ isMobile, subShown: showSub }) : null),
                }}
                menu={inlineMenu}
              />
            )}
          </div>
          {showSub && <span aria-hidden="true" style={S.barSub}>{subFit.text}</span>}
        </>
      )}
    </CaseBarShell>
  );
}

// Where the second provider's boundary currently sits, as a percent of the BAR.
//
// While the pointer is down this must follow the GHOST, or the handle stays
// pinned at the old departure while the hatch under it moves — the two would
// visibly separate mid-drag. `secondHatch` is the committed answer and is used
// whenever no gesture is in flight.
function axisBoundaryPercent(drag, c, committedPercent) {
  if (!drag.ghost) return committedPercent;
  const width = c.endMin - c.startMin;
  if (!(width > 0)) return committedPercent;
  const pct = ((drag.liveEnd - c.startMin) / width) * 100;
  return Math.max(0, Math.min(100, pct));
}
